import { ChevronDown } from "lucide-react";
import {
    getUnitCategoryConfiguration,
    isKnownUnitCategory,
} from "verdi-common/Configurations/Units/UnitCategory/UnitCategoryConfigurations";
import type { Unit } from "verdi-common/Configurations/Units/UnitConfigurations";
import { UnitConfigurations } from "verdi-common/Configurations/Units/UnitConfigurations";
import { getUnitCategoryName } from "verdi-common/i18n";
import {
    Menu,
    MenuGroup,
    MenuGroupLabel,
    MenuPopup,
    MenuRadioGroup,
    MenuRadioItem,
    MenuSeparator,
    MenuTrigger,
} from "@/components/ui/menu";
import { Tooltip, TooltipPopup, TooltipTrigger } from "@/components/ui/tooltip";
import { useSupportedLocale } from "@/hooks/use-locale";
import { cn } from "@/utils/class-name";
import type { AxisSpec } from "../../ChartSpec";
import { useDataViewerStore } from "../../data-viewer-provider";
import { type GetDisplayInfo, useGetDisplayInfo } from "../../units/use-get-display-info";
import { INK } from "../panel-tokens";
import type { LeftYAxisSelection } from "../use-left-y-axis-selection";

const CELL_CLASS = "inline-flex shrink-0 items-center whitespace-nowrap font-medium text-[12px] leading-[14px]";

function cellLabel(axis: AxisSpec, getDisplayInfo: GetDisplayInfo): string {
    const displayInfo = getDisplayInfo(axis.unitCategory);
    return axis.label ?? (displayInfo.isKnown ? displayInfo.symbol : axis.unitCategory);
}

/** The selection, when it has more than one left axis to switch between. */
function axisPickerFor(selection: LeftYAxisSelection | undefined): LeftYAxisSelection | undefined {
    return selection && selection.leftAxes.length > 1 ? selection : undefined;
}

/** Dimensionless units have an empty symbol, which would draw an empty cell, unless the cell is the way to pick the axis. */
export function hasUnitCell(
    axis: AxisSpec,
    getDisplayInfo: GetDisplayInfo,
    leftAxisSelection?: LeftYAxisSelection,
): boolean {
    return cellLabel(axis, getDisplayInfo) !== "" || axisPickerFor(leftAxisSelection) !== undefined;
}

/**
 * The header's unit cell for one axis. A menu when there is a unit or a left axis to pick, plain text
 * otherwise. Pass the left axis selection only on the left: its menu switches which axis is shown.
 */
export function UnitCell({ axis, leftAxisSelection }: { axis: AxisSpec; leftAxisSelection?: LeftYAxisSelection }) {
    const getDisplayInfo = useGetDisplayInfo();
    const setUnitOverride = useDataViewerStore((state) => state.setUnitOverride);
    const locale = useSupportedLocale();

    const { unitCategory } = axis;
    const displayInfo = getDisplayInfo(unitCategory);
    const label = cellLabel(axis, getDisplayInfo);
    const allowedUnits = getUnitCategoryConfiguration(unitCategory)?.allowedUnits ?? [];

    const axisPicker = axisPickerFor(leftAxisSelection);
    const hasAxisPicker = axisPicker !== undefined;
    const hasUnitPicker = displayInfo.isKnown && allowedUnits.length > 1;

    if (!hasAxisPicker && !hasUnitPicker) {
        return (
            <span className={CELL_CLASS} style={{ color: INK }}>
                {label}
            </span>
        );
    }

    const tooltip = hasAxisPicker ? "Choose y-axis" : "Change unit";

    return (
        <Menu>
            <Tooltip>
                <TooltipTrigger
                    render={
                        <MenuTrigger
                            aria-label={`${tooltip}, currently ${label}`}
                            className={cn(
                                CELL_CLASS,
                                "cursor-pointer gap-[4px] outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                            )}
                            style={{ color: INK }}
                        />
                    }
                >
                    {label}
                    <ChevronDown aria-hidden className={"size-[10px] shrink-0"} strokeWidth={3} />
                </TooltipTrigger>
                <TooltipPopup>{tooltip}</TooltipPopup>
            </Tooltip>
            <MenuPopup side={"bottom"} align={"start"} sideOffset={2}>
                {hasAxisPicker ? (
                    <MenuGroup>
                        <MenuGroupLabel>Metric</MenuGroupLabel>
                        <MenuRadioGroup
                            value={axis.id}
                            onValueChange={(axisId) => {
                                if (axisId) axisPicker.setActiveLeftAxisId(axisId);
                            }}
                        >
                            {axisPicker.leftAxes.map((candidate) => (
                                <MenuRadioItem key={candidate.id} value={candidate.id} closeOnClick={false}>
                                    {getUnitCategoryName(candidate.unitCategory, locale)}
                                </MenuRadioItem>
                            ))}
                        </MenuRadioGroup>
                    </MenuGroup>
                ) : null}
                {hasAxisPicker && hasUnitPicker ? <MenuSeparator /> : null}
                {hasUnitPicker ? (
                    <MenuGroup>
                        {hasAxisPicker ? <MenuGroupLabel>Unit</MenuGroupLabel> : null}
                        <MenuRadioGroup
                            value={displayInfo.unit}
                            onValueChange={(unit) => {
                                if (unit !== null && isKnownUnitCategory(unitCategory)) {
                                    setUnitOverride(unitCategory, unit as Unit);
                                }
                            }}
                        >
                            {allowedUnits.map((unit) => (
                                <MenuRadioItem key={unit} value={unit} closeOnClick>
                                    {UnitConfigurations[unit]?.symbol ?? unit}
                                </MenuRadioItem>
                            ))}
                        </MenuRadioGroup>
                    </MenuGroup>
                ) : null}
            </MenuPopup>
        </Menu>
    );
}
